/**
 * me/quiz/$activationId.tsx — Student live quiz take page (Phase H.1).
 *
 * Loads GET /api/quiz/activation/:id to get the quiz title (the WS
 * sends question state but not the title) + /api/auth/me to get
 * the userId + displayName for the WS join. Renders <QuizPlayer>
 * which owns the WS connection + answer flow.
 *
 * Auto-joins via POST /api/quiz/activation/:id/join so the student
 * has a quiz_attempt row before the first question arrives (the
 * DO's handleJoin is idempotent and also creates the attempt if
 * missing, but joining via REST up-front ensures the row exists
 * for the result page).
 *
 * Auth: any logged-in non-parent user can join. Parents get
 * redirected to /parent (they view child history instead).
 */

import { type ReactNode, useEffect, useState } from 'react';
import { Outlet, createFileRoute, redirect, useMatchRoute, useParams } from '@tanstack/react-router';
import { PageHeader } from '../../../components/ui/PageHeader.js';
import { StatePanel } from '../../../components/ui/StatePanel.js';
import { QuizPlayer } from '../../../components/student/QuizPlayer.js';
import { BackToSessionLink } from '../../../components/quiz/BackToSessionLink.js';
import { freshSessionQuery } from '../../../lib/api/queries.js';

export const Route = createFileRoute('/me/quiz/$activationId')({
  // `from` is set by the in-room LiveQuizBanner so the page can offer a
  // one-click way back into the ongoing RC session (BackToSessionLink).
  validateSearch: (search: Record<string, unknown>) => ({
    from: typeof search.from === 'string' ? search.from : undefined,
  }),
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({ to: '/login', search: { error: undefined, redirect: location.href } });
    }
    if (user.role === 'parent') {
      throw redirect({ to: '/parent' });
    }
    if (user.role === 'admin' || user.role === 'tutor') {
      // Admin/tutor shouldn't be taking quizzes; bounce them to their
      // own home. They CAN view via the live control panel.
      throw redirect({ to: user.role === 'admin' ? '/admin' : '/dashboard' });
    }
    return null;
  },
  component: StudentQuizPage,
});

interface ActivationSummary {
  activationId: string;
  classId: string;
  quizId: string;
  status: 'live' | 'ended';
  quizTitle: string;
}

function StudentQuizPage(): ReactNode {
  const { activationId } = useParams({ strict: false }) as { activationId: string };
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ActivationSummary | null>(null);
  const [userId, setUserId] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const matchRoute = useMatchRoute();
  const isOnResultRoute = matchRoute({ to: '/me/quiz/$activationId/result', fuzzy: true });
  const { from: fromParam } = Route.useSearch();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sumR, sessR, joinR] = await Promise.all([
          fetch(`/api/quiz/activation/${encodeURIComponent(activationId)}`, { credentials: 'include' }),
          fetch('/api/auth/me', { credentials: 'include' }),
          fetch(`/api/quiz/activation/${encodeURIComponent(activationId)}/join`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({}),
          }),
        ]);
        const sumJ = (await sumR.json()) as { ok: boolean; data?: ActivationSummary; error?: { message: string } };
        const sessJ = (await sessR.json()) as { ok?: boolean; data?: { user?: { userId: string; name: string } } };
        // joinR is best-effort — if the student is not a class member
        // the DO will reject the WS join anyway. The REST endpoint
        // returns a clean 403 which we ignore.
        void joinR;
        if (cancelled) return;
        if (!sumJ.ok || !sumJ.data) {
          setLoadError(sumJ.error?.message ?? 'Gagal memuat aktivasi');
          setLoading(false);
          return;
        }
        setSummary(sumJ.data);
        setUserId(sessJ.data?.user?.userId ?? '');
        setUserName(sessJ.data?.user?.name ?? '');
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setLoadError((err as Error).message);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activationId]);

  if (loading) {
    return (
      <main className="page-shell">
        <PageHeader eyebrow="Quiz" title="Memuat…" />
      </main>
    );
  }
  if (loadError || !summary) {
    return (
      <main className="page-shell">
        <PageHeader eyebrow="Quiz" title="Aktivasi tidak ditemukan" />
        <StatePanel variant="error" title="Gagal memuat" description={loadError ?? 'Tidak ditemukan'} />
      </main>
    );
  }

  if (isOnResultRoute) {
    // The result page is a nested child route at /me/quiz/$activationId/result.
    // Without this Outlet, the child component never mounts when the WS
    // pushes quiz:ended and QuizPlayer navigates to the result URL.
    // Discovered by QZ-03 E2E (subagent-reported 2026-07-31).
    return <Outlet />;
  }

  return (
    <main className="page-shell" data-testid="student-quiz">
      <div className="quiz-back-nav">
        <BackToSessionLink
          from={fromParam}
          classId={summary.classId}
          fallbackHref="/me"
          fallbackLabel="← Kembali ke dasbor"
        />
      </div>
      <QuizPlayer
        activationId={summary.activationId}
        userId={userId}
        displayName={userName}
        initialQuizTitle={summary.quizTitle}
        from={fromParam}
      />
    </main>
  );
}
